import { Box, Chip, Stack, chipClasses } from "@mui/material";
import React, { useState } from "react";
import { styled, useTheme } from "@mui/material/styles";
import Tooltip from "@hyro-components/Tooltip";
import CloseIcon from "src/icons/CloseIcon";

const CHIP_LIMIT = 1;

const StyledChipDeleteIcon = styled(Stack)(() => ({
  backgroundColor: "#2D2C443D",
  borderRadius: "50%",
  width: "18px",
  height: "18px",
  alignItems: "center",
  justifyContent: "center",
  "& svg": { width: "8px" },
  "& svg path": { strokeWidth: "3px" }
}));

const StyledChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== "isInvalid"
})(({ theme, isInvalid }) => ({
  border: isInvalid ? `1px solid ${theme.palette.error[500]}` : "1px solid rgba(45, 44, 68, 0.24)",
  fontSize: "12px",
  lineHeight: "18px",
  fontWeight: 500,
  [`& .${chipClasses.label}`]: {
    userSelect: "text"
  },
  "&:active": {
    backgroundColor: `${theme.palette.tertiary[200]} !important`,
    boxShadow: "none !important"
  }
}));

const StyledChipContainer = styled(Box)(() => ({
  display: "flex",
  flexWrap: "wrap",
  gap: "8px"
}));

const SingleChip = ({ option, index, testId, isReadOnly, onDelete, enableCopy = true, isInvalid, tooltip }) => {
  const [isCopied, setIsCopied] = useState(false);
  const theme = useTheme();

  const tooltipText = isCopied ? "Copied" : tooltip || "";

  const handleCopy = () => {
    navigator.clipboard.writeText(option);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 1000);
  };

  return (
    <Tooltip
      key={`${option}-${index}`}
      title={tooltipText}
      tooltipStyle={{ display: tooltipText ? "flex" : "none", alignItems: "center", height: "22px", lineHeight: "22px" }}
      disablePortal
    >
      <StyledChip
        data-testid={`${testId ? `${testId}-` : ""}chip-${index}`}
        variant="outlined"
        label={option}
        isInvalid={isInvalid}
        deleteIcon={isReadOnly ? <></> : <StyledChipDeleteIcon
          data-testid={`${testId ? `${testId}-` : ""}chip-${index}-delete-icon`}><CloseIcon
            color={theme.palette.system[0]} /></StyledChipDeleteIcon>}
        onDelete={onDelete?.(option)}
        onClick={enableCopy ? handleCopy : undefined}
      />
    </Tooltip >
  );
};

const ChipContainer = ({ values, showAllChips, testId, isReadOnly, onDelete, enableCopy, invalidIndexes }) => (<>
  {showAllChips ?
    <StyledChipContainer onMouseDown={(e) => e.preventDefault()}>
      {values.map((option, index) => (
        <SingleChip
          key={`chip-${index}`}
          option={option?.text || option}
          index={index}
          testId={testId}
          isReadOnly={isReadOnly}
          isInvalid={invalidIndexes?.includes(index)}
          tooltip={option?.tooltip}
          enableCopy={enableCopy}
          onDelete={onDelete}
        />
      ))}</StyledChipContainer>
    :
    <Box display="flex" justifyContent="flex-start">
      {values?.length > 0 &&
        <StyledChip
          label={values[0]}
          variant="outlined"
          isInvalid={invalidIndexes?.includes(0)}
          data-testid={`${testId ? `${testId}-` : ""}chip-0`} />}

      {values?.length > CHIP_LIMIT &&
        <StyledChip
          style={{ marginLeft: "8px", backgroundColor: "transparent" }}
          label={`+${values.length - CHIP_LIMIT}`}
          data-testid={`${testId ? `${testId}-` : ""}chip-count-box`} />}
    </Box>}
</>
);

export default ChipContainer;
